import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { logEvent } from "@src/appV2/lib/analytics";
import { useExpirableLocalStorage } from "@src/appV2/lib/utils/useExpirableLocalStorage";
import { getDeviceGeoLocationIfAllowed } from "@src/appV2/Location/deviceLocation/geoLocation";
import { calculateEstimatedDrivingDistanceInMiles } from "@src/appV2/Location/utils/geoDistance";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { useEffect, useRef } from "react";

const LOCATION_CHANGE_PROMPT_STORAGE_NAMESPACE = "location-change-prompt";
const LOCATION_CHANGE_PROMPT_DISMISSED_KEY = "dismissed";

const DEFAULT_CONFIG = {
  enabled: false,
  cooldownInHours: 72,
  distanceThresholdInMiles: 300,
};

interface UseLocationChangePromptReturn {
  modalState: ReturnType<typeof useModalState>;
  distanceInMiles: number | undefined;
  cooldownInMs: number;
}

export function useLocationChangePrompt(): UseLocationChangePromptReturn {
  const modalState = useModalState();
  const workerGeoLocation = useWorkerGeoLocation();
  const distanceRef = useRef<number | undefined>(undefined);
  const hasCheckedRef = useRef(false);

  const { enabled, cooldownInHours, distanceThresholdInMiles } = useCbhFlag(
    CbhFeatureFlag.LOCATION_CHANGE_PROMPT_CONFIG,
    {
      defaultValue: DEFAULT_CONFIG,
    }
  );

  const cooldownInMs = cooldownInHours * 60 * 60 * 1000;

  const { getValue } = useExpirableLocalStorage<boolean>(LOCATION_CHANGE_PROMPT_STORAGE_NAMESPACE);

  const { setModalStatus } = modalState;

  useEffect(() => {
    if (hasCheckedRef.current) {
      logEvent("Location Change Prompt: Already checked, skipping");
      return;
    }

    if (!enabled) {
      logEvent("Location Change Prompt: Feature flag not enabled", {
        enabled,
        cooldownInHours,
        distanceThresholdInMiles,
      });
      return;
    }

    if (!isDefined(workerGeoLocation)) {
      logEvent("Location Change Prompt: Worker geo location not available");
      return;
    }

    hasCheckedRef.current = true;

    const isDismissed = getValue(LOCATION_CHANGE_PROMPT_DISMISSED_KEY) === true;
    if (isDismissed) {
      logEvent("Location Change Prompt: Previously dismissed, skipping");
      return;
    }

    logEvent("Location Change Prompt: Starting distance check", {
      workerLat: workerGeoLocation.latitude,
      workerLng: workerGeoLocation.longitude,
    });

    const capturedWorkerGeoLocation = workerGeoLocation;

    async function checkDistance() {
      try {
        const deviceLocation = await getDeviceGeoLocationIfAllowed();
        if (!isDefined(deviceLocation)) {
          logEvent("Location Change Prompt: Device location unavailable");
          return;
        }

        const distance = calculateEstimatedDrivingDistanceInMiles(
          capturedWorkerGeoLocation,
          deviceLocation.geoLocation
        );

        distanceRef.current = distance;

        logEvent("Location Change Prompt: Distance checked", {
          distanceInMiles: distance,
          thresholdInMiles: distanceThresholdInMiles,
          shouldPrompt: distance > distanceThresholdInMiles,
        });

        if (distance > distanceThresholdInMiles) {
          setModalStatus("open");
        }
      } catch (error) {
        logEvent("Location Change Prompt: Check distance error", {
          error: String(error),
        });
      }
    }

    void checkDistance();
  }, [
    workerGeoLocation,
    enabled,
    cooldownInHours,
    distanceThresholdInMiles,
    getValue,
    setModalStatus,
  ]);

  return {
    modalState,
    distanceInMiles: distanceRef.current,
    cooldownInMs,
  };
}

export function useDismissLocationChangePrompt() {
  const { setValue } = useExpirableLocalStorage<boolean>(LOCATION_CHANGE_PROMPT_STORAGE_NAMESPACE);

  return (cooldownInMs: number) => {
    setValue(LOCATION_CHANGE_PROMPT_DISMISSED_KEY, true, cooldownInMs);
  };
}
